import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, User, Bell, Globe, Shield } from 'lucide-react';
import { LogoFull } from '../components/Logo';
import { authAPI } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Settings = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading user:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t.settings.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <LogoFull iconClassName="w-9 h-9" textClassName="text-2xl" />
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-8">{t.settings.title}</h1>

        <div className="space-y-6">
          {/* Profile Section */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="w-5 h-5" />
                <span>{t.settings.profile}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">{t.settings.username}</label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg">
                  {user?.username}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t.settings.email}</label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg">
                  {user?.email}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t.settings.status}</label>
                <div className="mt-1">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    user?.subscription_status === 'premium' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user?.subscription_status === 'premium' ? `👑 ${t.settings.premium}` : `🆓 ${t.settings.free}`}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Language Section */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Globe className="w-5 h-5" />
                <span>{t.settings.language}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                {t.settings.languageDescription}
              </p>
              <LanguageSwitcher />
            </CardContent>
          </Card>

          {/* Notifications Section */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="w-5 h-5" />
                <span>{t.settings.notifications}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                {t.settings.notificationsDescription}
              </p>
            </CardContent>
          </Card>

          {/* Privacy Section */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span>{t.settings.privacy}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => window.open('#', '_blank')}
              >
                {t.settings.privacyPolicy}
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => window.open('#', '_blank')}
              >
                {t.settings.termsOfService}
              </Button>
            </CardContent>
          </Card>

          {/* Back Button */}
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          >
            {t.settings.backToDashboard}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
